import {
  adCommentMessageTypes,
  commentMessageTypes,
  COMMUNITY_ADS_INTERACTION_TYPES,
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_MESSAGE_SUBTYPES,
  conversationMessageTypes,
  mentionMessageTypes,
} from '@/app/community/constants';
import { sentimentColors } from '@/config';

export const dateTimeFormat = 'YYYY-MM-DDTHH:mm:ss';

export const communityStartDate = '2000-01-01';

export const COMMUNITY_FILTER_TOOLTIPS = Object.freeze({
  REPORTING_PERIOD: {
    INSIGHTS: 'Activity on messages received within the reporting period',
    MESSAGES: 'Messages received within the reporting period',
    INTERNAL_NOTES: 'Internal notes created within the reporting period',
  },
  TAG: 'Filter by messages that match any or all applied tags',
  MESSAGE_STATE: 'Filter by messages that match all applied states',
  BRANDS: {
    FILTER_PANEL: 'Filter by messages within specific brand inboxes',
    FILTER_TYPE: {
      BRANDS: 'Clear the selected brand tags to choose brands',
      BRAND_TAGS: 'Clear the selected brands to choose brand tags',
    },
    CUSTOM_VIEW: {
      BRAND_TAGS_DISABLED: 'Brand tags are not available in custom inbox views',
    },
  },
  MESSAGE_CONTENT:
    'Matches messages made up of a single element, like an emoji or mention. In DMs, it applies only to the user’s most recent message.',
  SENTIMENT: 'Applies only to Comments, Mentions and Quote Posts',
  SENTIMENT_WITH_DMS:
    'Detected sentiment from message text. Excludes DMs where sentiment couldn’t be determined (e.g., unsupported content, reel shares, story mentions, or post shares).',
});

export const COMMUNITY_FILTER_MESSAGES = Object.freeze({
  POST: 'Your messages will be filtered by the posts you select.',
  EMPTY_MESSAGE_RULE: 'Get started with inbox automation by creating your first automation rule.',
});

export const CHANNEL_FILTER_OPTIONS = Object.freeze({
  FACEBOOK: 'FACEBOOK',
  INSTAGRAM: 'INSTAGRAM',
  TIKTOK: 'TIKTOK',
  TWITTER: 'TWITTER',
  YOUTUBE: 'YOUTUBE',
});

export const CHANNEL_FILTER_INTERACTION_TYPE_MAP = Object.freeze({
  FACEBOOK: [
    COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT,
    COMMUNITY_ADS_INTERACTION_TYPES.FACEBOOK_ADS_COMMENT,
    COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION,
  ],
  INSTAGRAM: [
    COMMUNITY_INTERACTION_TYPES.INSTAGRAM_COMMENT,
    COMMUNITY_ADS_INTERACTION_TYPES.INSTAGRAM_ADS_COMMENT,
    COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION,
  ],
  TIKTOK: [
    COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT,
    COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION,
  ],
  TWITTER: [
    COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION,
    COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE,
    COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET,
    COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION,
  ],
  YOUTUBE: [COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT],
});

export const MESSAGE_TYPE_FILTER_OPTIONS = Object.freeze({
  ADS_COMMENT: {
    label: 'Ad Comments',
    value: 'ADS_COMMENT',
    platforms: [CHANNEL_FILTER_OPTIONS.FACEBOOK, CHANNEL_FILTER_OPTIONS.INSTAGRAM],
    includes: adCommentMessageTypes(),
    info: 'Applies to boosted and dark posts on Instagram and Facebook',
    statsValue: 'AD_COMMENTS',
  },
  COMMENT: {
    label: 'Comments',
    value: 'COMMENT',
    platforms: [
      CHANNEL_FILTER_OPTIONS.FACEBOOK,
      CHANNEL_FILTER_OPTIONS.INSTAGRAM,
      CHANNEL_FILTER_OPTIONS.TIKTOK,
      CHANNEL_FILTER_OPTIONS.YOUTUBE,
    ],
    includes: commentMessageTypes(),
    statsValue: 'COMMENTS',
  },
  CONVERSATION: {
    label: 'Direct Messages',
    formattedLabel: 'DMs',
    value: 'CONVERSATION',
    platforms: [
      CHANNEL_FILTER_OPTIONS.FACEBOOK,
      CHANNEL_FILTER_OPTIONS.INSTAGRAM,
      CHANNEL_FILTER_OPTIONS.TWITTER,
    ],
    includes: conversationMessageTypes(),
    statsValue: 'DMS',
  },
  MENTION: {
    label: 'Mentions',
    value: 'MENTION',
    insightsPlatforms: [CHANNEL_FILTER_OPTIONS.TWITTER],
    platforms: [CHANNEL_FILTER_OPTIONS.TIKTOK, CHANNEL_FILTER_OPTIONS.TWITTER],
    includes: mentionMessageTypes(),
    insightsInfo: 'Applicable to X',
    info: 'Applicable to X and TikTok',
    statsValue: 'MENTIONS',
  },
  QUOTE: {
    label: 'Quote Posts',
    formattedLabel: 'Quote Posts',
    value: 'QUOTE',
    platforms: [CHANNEL_FILTER_OPTIONS.TWITTER],
    includes: [COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE],
    statsValue: 'QUOTE_POSTS',
  },
  RETWEET: {
    label: 'Reposts',
    value: 'RETWEET',
    platforms: [CHANNEL_FILTER_OPTIONS.TWITTER],
    includes: [COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET],
    statsValue: 'REPOSTS',
  },
  STORY_MENTION: {
    label: 'Story Mentions',
    value: 'STORY_MENTION',
    platforms: [CHANNEL_FILTER_OPTIONS.INSTAGRAM],
    subtype: true,
    info: 'Applies to Instagram Stories',
    includes: [COMMUNITY_MESSAGE_SUBTYPES.STORY_MENTION],
    statsValue: 'STORY_MENTION',
  },
});

export const SENTIMENT_FILTER_OPTIONS = Object.freeze({
  POSITIVE: {
    label: 'Positive',
    value: 'POSITIVE',
    severity: 'positive',
    field: 'isPositive',
    color: sentimentColors.positive,
  },
  NEUTRAL: {
    label: 'Neutral',
    value: 'NEUTRAL',
    severity: 'neutral',
    field: 'isNeutral',
    color: sentimentColors.neutral,
  },
  NEGATIVE: {
    label: 'Negative',
    value: 'NEGATIVE',
    severity: 'negative',
    field: 'isNegative',
    color: sentimentColors.negative,
  },
});

export const MESSAGE_STATE_FILTER_OPTIONS = Object.freeze({
  // TODO: DM_REFACTOR_2026 - Remove NOT_ARCHIVED, use NOT_CLOSED
  NOT_ARCHIVED: {
    label: 'Not Archived',
    value: 'NOT_ARCHIVED',
    queryKey: 'isArchived',
    queryValue: false,
    icon: 'inbox',
  },
  NOT_CLOSED: {
    label: 'Not Closed',
    value: 'NOT_CLOSED',
    queryKey: 'isArchived',
    queryValue: false,
    icon: 'check-circle',
  },
  UNREAD: {
    label: 'Unread',
    value: 'UNREAD',
    queryKey: 'isRead',
    queryValue: false,
    icon: 'mail-unread',
  },
  FOLLOW_UP: {
    label: 'Marked as Follow Up',
    value: 'FOLLOW_UP',
    queryKey: 'isFollowUp',
    queryValue: true,
    icon: 'bookmark',
  },
  RESPONSE_RECOMMENDED: {
    label: 'Response Recommended',
    value: 'RESPONSE_RECOMMENDED',
    queryKey: 'responseRecommended',
    queryValue: true,
    icon: 'response-recommended',
  },
  REPLIED_TO: {
    label: 'Replied To',
    value: 'REPLIED_TO',
    queryKey: 'isReplied',
    queryValue: true,
    icon: 'reply',
  },
  // Filters deleted non-DM interactions only; hidden comments are not in scope.
  DELETED_COMMENT: {
    label: 'Deleted Comment',
    value: 'DELETED_COMMENT',
    queryKey: 'isDeleted',
    queryValue: true,
    icon: 'delete',
  },
});

export const MESSAGE_CONTENT_FILTER_OPTIONS = Object.freeze({
  EMOJI_ONLY: {
    label: 'Emoji Only',
    value: 'EMOJI_ONLY',
    icon: 'emoji',
  },
  GIF_ONLY: {
    label: 'GIF Only',
    value: 'GIF_ONLY',
    icon: 'video',
    info: 'Applies only to Instagram and Facebook messages',
  },
  HASHTAG_ONLY: {
    label: 'Hashtag Only',
    value: 'HASHTAG_ONLY',
    icon: 'hashtag',
  },
  ONE_WORD_ONLY: {
    label: 'One Word Only',
    value: 'ONE_WORD_ONLY',
    icon: 'type',
    info: 'Applies to messages made up of just one word or string, which may include letters, numbers, or special characters',
  },
  LINK_ONLY: {
    label: 'Link Only',
    value: 'LINK_ONLY',
    icon: 'link',
  },
  MENTION_ONLY: {
    label: 'Mention Only',
    value: 'MENTION_ONLY',
    icon: 'at-sign',
  },
  QUESTION: {
    label: 'Question',
    value: 'QUESTION',
    icon: 'message-question',
    queryKey: 'isQuestion',
    ruleKey: 'is_question',
    info: 'Applies only to Comments, Mentions and Quote Posts',
  },
  SPAM: {
    label: 'Spam',
    value: 'SPAM',
    icon: 'message-x',
    queryKey: 'isSpam',
    ruleKey: 'is_spam',
    info: 'Applies to messages automatically marked as spam by AI or manually by users',
  },
  UNSUPPORTED: {
    label: 'Unsupported',
    value: 'UNSUPPORTED',
    icon: 'x-circle',
  },
});

export const MESSAGE_CONTENT_FILTER_FLAG_TYPE_OPTIONS = Object.freeze([
  MESSAGE_CONTENT_FILTER_OPTIONS.QUESTION.value,
  MESSAGE_CONTENT_FILTER_OPTIONS.SPAM.value,
]);

export const SOURCE_UPDATED_FILTER_OPTIONS = Object.freeze({
  LAST_7_DAYS: {
    label: 'Last 7 Days',
    value: 'LAST_7_DAYS',
    subtractUnit: 'day',
    subtractValue: 6,
  },
  LAST_4_WEEKS: {
    label: 'Last 4 Weeks',
    value: 'LAST_4_WEEKS',
    subtractUnit: 'day',
    subtractValue: 27,
  },
  LAST_WEEK: { label: 'Last Week', value: 'LAST_WEEK', subtractUnit: 'week', subtractValue: 1 },
  LAST_MONTH: { label: 'Last Month', value: 'LAST_MONTH', subtractUnit: 'month', subtractValue: 1 },
});

export const ALL_TIME_SOURCE_UPDATED_OPTION = { label: 'All Time', value: 'ALL_TIME' };

export const FILTER_QUERY_PARAMS = Object.freeze({
  platforms: { type: Array, elementType: String },
  sentiments: { type: Array, elementType: String },
  messageTypes: { type: Array, elementType: String },
  mediaIds: { type: Array, elementType: Number },
  tagIds: { type: Array, elementType: Number },
  tagOperator: { type: String },
  mentionedUserIds: { type: Number },
  assigneeId: { type: Number },
  assigneeIds: { type: Array, elementType: Number },
  interactionRuleIds: { type: Array, elementType: Number },
  startDate: { type: String },
  endDate: { type: String },
  query: { type: String },
  sort: { type: String },
  isRead: { type: Boolean },
  isFollowUp: { type: Boolean },
  isArchived: { type: Boolean },
  isReplied: { type: Boolean },
  isDeleted: { type: Boolean },
  responseRecommended: { type: Boolean },
  brandIds: { type: Array, elementType: Number },
  brandTagIds: { type: Array, elementType: Number },
  messageContent: { type: Array, elementType: String },
});

export const MESSAGE_TAG_FILTER_TYPES = Object.freeze({
  AND: 'AND',
  OR: 'OR',
});

export const MESSAGE_TAG_FILTER_TYPE_OPTIONS = Object.freeze({
  [MESSAGE_TAG_FILTER_TYPES.OR]: {
    label: 'Match Any',
    value: MESSAGE_TAG_FILTER_TYPES.OR,
  },
  [MESSAGE_TAG_FILTER_TYPES.AND]: {
    label: 'Match All',
    value: MESSAGE_TAG_FILTER_TYPES.AND,
  },
});

export const FILTER_DETAILS = Object.freeze({
  COMMUNITY: {
    scope: 'community',
    options: {
      platforms: Object.values(CHANNEL_FILTER_OPTIONS),
      messageTypes: Object.values(MESSAGE_TYPE_FILTER_OPTIONS),
      sentiments: Object.values(SENTIMENT_FILTER_OPTIONS),
      messageState: Object.values(MESSAGE_STATE_FILTER_OPTIONS),
      messageContent: Object.values(MESSAGE_CONTENT_FILTER_OPTIONS),
    },
    default: {
      platforms: [],
      messageTypes: [],
      sourceUpdated: {
        preset: SOURCE_UPDATED_FILTER_OPTIONS.LAST_4_WEEKS.value,
      },
      mediaIds: [],
      tagIds: [],
      tagOperator: MESSAGE_TAG_FILTER_TYPES.OR,
      mentionedUserIds: undefined,
      messageState: [],
      assigneeIds: [],
      interactionRuleIds: [],
      sentiments: [],
      brandIds: [],
      brandTagIds: [],
      sort: undefined,
      query: undefined,
      messageContent: [],
    },
  },
});

export const BRAND_FILTER_TYPES = Object.freeze({
  BRANDS: 'brandIds',
  BRAND_TAGS: 'brandTagIds',
});

export const BRAND_FILTER_TYPE_OPTIONS = Object.freeze({
  [BRAND_FILTER_TYPES.BRANDS]: {
    label: 'Brands',
    value: BRAND_FILTER_TYPES.BRANDS,
    disabledBy: BRAND_FILTER_TYPES.BRAND_TAGS,
    disabledTooltip: COMMUNITY_FILTER_TOOLTIPS.BRANDS.FILTER_TYPE.BRANDS,
  },
  [BRAND_FILTER_TYPES.BRAND_TAGS]: {
    label: 'Brand Tags',
    value: BRAND_FILTER_TYPES.BRAND_TAGS,
    disabledBy: BRAND_FILTER_TYPES.BRANDS,
    disabledTooltip: COMMUNITY_FILTER_TOOLTIPS.BRANDS.FILTER_TYPE.BRAND_TAGS,
  },
});

export const BOOLEAN_TYPE_VIEW_FILTER_QUERY_MAP = Object.freeze({
  isQuestion: 'messageContent',
  isSpam: 'messageContent',
  isPositive: 'sentiments',
  isNeutral: 'sentiments',
  isNegative: 'sentiments',
});

export const DEFAULT_VIEW_FILTER = Object.freeze({
  platforms: [],
  brandIds: [],
  messageTypes: [],
  tagIds: [],
  sentiments: [],
  messageContent: [],
  assigneeIds: [],
  tagOperator: MESSAGE_TAG_FILTER_TYPES.OR,
});

export const VIEW_FILTER_QUERY_PARAMS = Object.freeze({
  brandIds: { type: Array, elementType: Number },
  types: { type: Array, elementType: String },
  isPositive: { type: Boolean },
  isNeutral: { type: Boolean },
  isNegative: { type: Boolean },
  messageContent: { type: Array, elementType: String },
  isSpam: { type: Boolean },
  isQuestion: { type: Boolean },
  assigneeIds: { type: Array, elementType: Number },
  tagIds: { type: Array, elementType: Number },
  tagOperator: { type: String },
  subtype: { type: Array, elementType: String },
});

export const INTERACTION_TYPE_MAP = Object.freeze({
  INSTAGRAM_COMMENT: {
    platform: CHANNEL_FILTER_OPTIONS.INSTAGRAM,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.COMMENT.value,
  },
  INSTAGRAM_ADS_COMMENT: {
    platform: CHANNEL_FILTER_OPTIONS.INSTAGRAM,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.ADS_COMMENT.value,
  },
  INSTAGRAM_CONVERSATION: {
    platform: CHANNEL_FILTER_OPTIONS.INSTAGRAM,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.CONVERSATION.value,
  },
  STORY_MENTION: {
    platform: CHANNEL_FILTER_OPTIONS.INSTAGRAM,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.STORY_MENTION.value,
  },
  TIKTOK_COMMENT: {
    platform: CHANNEL_FILTER_OPTIONS.TIKTOK,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.COMMENT.value,
  },
  TIKTOK_COMMENT_MENTION: {
    platform: CHANNEL_FILTER_OPTIONS.TIKTOK,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.MENTION.value,
  },
  FACEBOOK_COMMENT: {
    platform: CHANNEL_FILTER_OPTIONS.FACEBOOK,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.COMMENT.value,
  },
  FACEBOOK_ADS_COMMENT: {
    platform: CHANNEL_FILTER_OPTIONS.FACEBOOK,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.ADS_COMMENT.value,
  },
  FACEBOOK_CONVERSATION: {
    platform: CHANNEL_FILTER_OPTIONS.FACEBOOK,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.CONVERSATION.value,
  },
  TWITTER_MENTION: {
    platform: CHANNEL_FILTER_OPTIONS.TWITTER,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.MENTION.value,
  },
  TWITTER_RETWEET: {
    platform: CHANNEL_FILTER_OPTIONS.TWITTER,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.RETWEET.value,
  },
  TWITTER_QUOTE: {
    platform: CHANNEL_FILTER_OPTIONS.TWITTER,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.QUOTE.value,
  },
  TWITTER_CONVERSATION: {
    platform: CHANNEL_FILTER_OPTIONS.TWITTER,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.CONVERSATION.value,
  },
  YOUTUBE_COMMENT: {
    platform: CHANNEL_FILTER_OPTIONS.YOUTUBE,
    messageType: MESSAGE_TYPE_FILTER_OPTIONS.COMMENT.value,
  },
});
